import { useMemo, useState, type ReactElement } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Cable, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AsyncSection,
  Badge,
  Button,
  EmptyState,
  Input,
  SectionHeader,
  SettingsGroup,
  Skeleton,
} from "@playbookai/ui";
import {
  createIntegrationConnectionsApi,
  useApiClient,
} from "@playbookai/api-clients";
import { SETTINGS_CONNECTIONS_ROUTE } from "../../routes.js";
import { connectRefusalMessage } from "./connect-refusal.js";
import { ProviderMark } from "./ConnectionsSettings.js";
import { useConnectionAuthorization } from "./use-connection-authorization.js";

/** Member-facing catalogue of administrator-approved connection instances. */
export function AddConnectionSettings(): ReactElement {
  const config = useApiClient();
  const api = useMemo(() => createIntegrationConnectionsApi(config), [config]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const instances = useQuery({
    queryKey: ["connectable-integrations"],
    queryFn: () => api.listConnectable(),
  });
  const connections = useQuery({
    queryKey: ["integration-connections"],
    queryFn: () => api.listMine(),
  });
  const refreshAfterAuthorization = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["integration-connections"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["connectable-integrations"],
      }),
    ]);
  };
  const authorization = useConnectionAuthorization(
    api,
    refreshAfterAuthorization,
  );

  if (instances.isLoading || connections.isLoading) {
    /* The wait through AsyncSection's loading branch: its `role="status"`
       region is named "Loading available connections". A failed read renders
       the page below with its own notice. */
    return (
      <AsyncSection
        status="loading"
        loadingLabel="available connections"
        errorTitle="Couldn't load the available connections"
        skeleton={
          <div className="flex flex-col gap-3">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-10 w-65" />
            {/* Two columns from the page's own width, not the window: the
                Player AI side rail narrows the page without changing the
                window (PB-5436). The loaded list below uses the same rule. */}
            <div className="grid gap-3 @min-[42.5rem]/settings-page:grid-cols-2">
              <Skeleton shape="card" className="h-36" />
              <Skeleton shape="card" className="h-36" />
            </div>
          </div>
        }
      >
        {null}
      </AsyncSection>
    );
  }

  const needle = search.trim().toLocaleLowerCase();
  const visible = (instances.data ?? []).filter((instance) =>
    `${instance.displayName} ${instance.description ?? ""}`
      .toLocaleLowerCase()
      .includes(needle),
  );
  const currentConnections = connections.data ?? [];

  return (
    <div className="pb-motion-content-enter">
      <Button
        variant="ghost"
        size="sm"
        className="mb-3.5 -ms-2"
        onClick={() => navigate(SETTINGS_CONNECTIONS_ROUTE)}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Your connections
      </Button>

      <SectionHeader
        variant="settings"
        leading={<Cable className="w-4 h-4 text-pb-cyan-text" />}
        title="Add a connection"
        description="Connect an account from the integrations your admin has made available."
      />

      {authorization.watching ? (
        <Alert tone="info" title="Waiting for authorization">
          Finish connecting in the new tab. This catalogue updates automatically
          when authorization completes.
        </Alert>
      ) : null}
      {authorization.error !== null ? (
        <div className="mt-4">
          <Alert tone="critical" title="Could not start the connection">
            {connectRefusalMessage(authorization.error)}
          </Alert>
        </div>
      ) : null}
      {authorization.windowError !== null ? (
        <div className="mt-4">
          <Alert tone="critical" title="Authorization window unavailable">
            {authorization.windowError}
          </Alert>
        </div>
      ) : null}
      {instances.isError || connections.isError ? (
        <div className="mt-4">
          <Alert
            tone="critical"
            title="Couldn't load the available connections"
          >
            {((instances.error ?? connections.error) as Error | null)?.message}
            {/* A button, not an instruction to reload the browser. */}
            <Button
              variant="secondary"
              onClick={() => {
                void instances.refetch();
                void connections.refetch();
              }}
            >
              Retry
            </Button>
          </Alert>
        </div>
      ) : null}

      <div className="max-w-65">
        <Input
          type="search"
          label="Search available connections"
          placeholder="Search by provider or connection name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          prefix={<Search className="h-4 w-4" aria-hidden />}
        />
      </div>

      <div className="mt-6">
        <SettingsGroup variant="settings" title="Available connections">
          {visible.length === 0 ? (
            <div className="mt-3">
              <EmptyState
                icon={<Cable className="h-5 w-5" />}
                title={
                  needle === ""
                    ? "Nothing to connect to"
                    : "No connections found"
                }
                description={
                  needle === ""
                    ? "Your admin has not enabled any integrations for you yet."
                    : "Try a different search."
                }
              />
            </div>
          ) : (
            <ul className="mt-3 grid gap-3 @min-[42.5rem]/settings-page:grid-cols-2">
              {visible.map((instance) => {
                const connectionCount = currentConnections.filter(
                  (connection) =>
                    connection.tenantIntegrationId === instance.id &&
                    connection.status !== "revoked",
                ).length;
                return (
                  /* Kept hand-built: Card has no px-4 py-3.75 padding
                   option and paints no hover border (interactive lifts it
                   with a shadow); no PR 3 look row approves either. */
                  <li
                    key={instance.id}
                    className="flex flex-col rounded-xl border border-pb-border/80 bg-pb-card px-4 py-3.75 transition-colors hover:border-pb-border-strong"
                  >
                    <div className="mb-2.25 flex items-center gap-2.75">
                      <ProviderMark providerName={instance.displayName} />
                      <h3 className="min-w-0 flex-1 truncate text-title text-pb-text">
                        {instance.displayName}
                      </h3>
                      {/* Kept restyled: Badge has no 4px corner, dense-2
                        semibold step or iris-interactive paint; no PR 3 look
                        row approves the soft ai tone's look. */}
                      <Badge
                        variant="outline"
                        className={
                          instance.protocol === "mcp"
                            ? "rounded-sm border-pb-iris-interactive/40 bg-pb-iris-interactive/10 text-dense-2 font-semibold text-pb-iris-text"
                            : "rounded-sm border-pb-border-strong text-dense-2 font-semibold text-pb-text-muted"
                        }
                      >
                        {instance.protocol === "mcp"
                          ? "Managed MCP"
                          : "Managed connection"}
                      </Badge>
                    </div>
                    {instance.description ? (
                      <p className="flex-1 text-caption text-pb-text-muted">
                        {instance.description}
                      </p>
                    ) : (
                      <span className="flex-1" />
                    )}
                    <div className="mt-3.25 flex flex-wrap items-center justify-between gap-2.5">
                      <span className="text-caption text-pb-text-muted">
                        {connectionCount === 0
                          ? "No accounts connected"
                          : `${connectionCount} ${connectionCount === 1 ? "account" : "accounts"} connected`}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={authorization.isPending}
                        onClick={() => authorization.start(instance.id)}
                      >
                        {connectionCount > 0 ? "Connect another" : "Connect"}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Inside the group so the group's bottom space (mb-7.5) falls after
            it, not between the list and this note. */}
          <p className="mt-4 text-body-small text-pb-text-muted">
            You can connect more than one account to the same system. Each
            account remains separate and uses its own permissions.
          </p>
        </SettingsGroup>
      </div>
    </div>
  );
}
